import { useEffect, useRef, useState } from "react";
import { Mic, Square } from "lucide-react";
import { dictationApi, type DictationResponse } from "../../../shared/api/dictation";
import { apiError } from "../../../shared/api/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface Props {
  encounterId: string;
  chiefComplaint?: string | null;
  patientAgeYears?: number | null;
  patientSexLabel?: string | null;
  open: boolean;
  onClose: () => void;
  onApply: (draft: DictationResponse) => void;
}

type Phase = "consent" | "armed" | "recording" | "uploading" | "done" | "error";

/**
 * Ambient-scribe modal — record an encounter, watch the server come back with
 * a SOAP draft, paste it into the chart with one click.
 *
 * <p>The PDPD consent step is the first screen and is mandatory; the recorder
 * is never armed without it. We use the built-in MediaRecorder API (no
 * external library) so we don't pull in a heavy dep just for this modal.
 */
export function DictationModal({
  encounterId,
  chiefComplaint,
  patientAgeYears,
  patientSexLabel,
  open,
  onClose,
  onApply,
}: Props) {
  const [phase, setPhase] = useState<Phase>("consent");
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState<DictationResponse | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const tickRef = useRef<number | null>(null);

  useEffect(() => {
    if (!open) {
      stopTick();
      cleanupRecorder();
      setPhase("consent");
      setError(null);
      setElapsed(0);
      setResult(null);
    }
    return () => {
      stopTick();
      cleanupRecorder();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function stopTick() {
    if (tickRef.current != null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }

  function cleanupRecorder() {
    const r = recorderRef.current;
    if (r && r.state !== "inactive") {
      try {
        r.stop();
      } catch {
        // already stopped
      }
    }
    if (r) {
      r.stream.getTracks().forEach((t) => t.stop());
    }
    recorderRef.current = null;
    chunksRef.current = [];
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Pick a codec the browser supports; webm/opus is universal in Chromium
      // and Firefox; Safari falls back to whatever the default mimeType is.
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "";
      const r = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      r.ondataavailable = (ev) => {
        if (ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      r.onerror = () => setError("Lỗi thu âm. Hãy thử lại.");
      r.onstop = () => void uploadAndTranscribe(r.mimeType || "audio/webm");
      r.start(1000); // capture in 1-second chunks
      recorderRef.current = r;
      setPhase("recording");
      setElapsed(0);
      tickRef.current = window.setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (e) {
      setError(
        "Không thể truy cập micro. Cấp quyền cho trình duyệt và thử lại."
      );
      setPhase("error");
    }
  }

  function stopRecording() {
    const r = recorderRef.current;
    stopTick();
    if (r && r.state !== "inactive") {
      r.stop();
    }
    setPhase("uploading");
  }

  async function uploadAndTranscribe(mimeType: string) {
    try {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      chunksRef.current = [];
      const res = await dictationApi.submit(encounterId, blob, {
        consentGiven: true,
        chiefComplaint: chiefComplaint ?? null,
        patientAgeYears: patientAgeYears ?? null,
        patientSexLabel: patientSexLabel ?? null,
        filename: mimeType.includes("webm") ? "encounter.webm" : "encounter.ogg",
      });
      setResult(res);
      setPhase("done");
    } catch (e) {
      const err = apiError(e);
      setError(err.detail || err.title);
      setPhase("error");
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Thu âm bệnh án (AI Scribe)</DialogTitle>
          {phase === "consent" && (
            <DialogDescription>
              Hệ thống sẽ thu âm cuộc trao đổi với bệnh nhân, dùng AI để tạo bản
              nháp <strong className="font-semibold">S–O–A–P</strong>, và lưu
              trữ tệp ghi âm để cải tiến mô hình. Bệnh nhân cần đồng ý theo Nghị
              định 13/2023 (Bảo vệ dữ liệu cá nhân).
            </DialogDescription>
          )}
        </DialogHeader>

        {phase === "consent" && (
          <div className="space-y-4">
            <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              <li>Có thể dừng thu âm bất kỳ lúc nào.</li>
              <li>Bản nháp do AI tạo cần bác sĩ kiểm tra trước khi lưu.</li>
              <li>
                Tệp ghi âm được lưu an toàn theo tenant, không chia sẻ ra ngoài.
              </li>
            </ul>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Hủy
              </Button>
              <Button type="button" onClick={() => setPhase("armed")}>
                Đã xác nhận đồng ý
              </Button>
            </DialogFooter>
          </div>
        )}

        {phase === "armed" && (
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <p className="text-sm text-muted-foreground">
              Khi sẵn sàng, nhấn{" "}
              <strong className="font-semibold text-foreground">Bắt đầu</strong>{" "}
              để thu âm. Hãy nói rõ về triệu chứng, dấu hiệu khám, chẩn đoán dự
              kiến và kế hoạch điều trị.
            </p>
            <Button type="button" size="lg" onClick={startRecording}>
              <Mic aria-hidden="true" />
              Bắt đầu
            </Button>
          </div>
        )}

        {phase === "recording" && (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <div className="flex items-center gap-2 text-3xl font-semibold tabular-nums text-destructive">
              <span aria-hidden="true">●</span>
              {formatElapsed(elapsed)}
            </div>
            <p className="text-sm text-muted-foreground">Đang thu âm…</p>
            <Button type="button" onClick={stopRecording}>
              <Square aria-hidden="true" />
              Dừng &amp; tạo bản nháp
            </Button>
          </div>
        )}

        {phase === "uploading" && (
          <div className="space-y-1 py-8 text-center">
            <p className="text-sm text-foreground">
              Đang tải lên và nhận dạng giọng nói…
            </p>
            <p className="text-xs text-muted-foreground">
              Quá trình này có thể mất 30–60 giây với mỗi 5 phút thu âm.
            </p>
          </div>
        )}

        {phase === "done" && result && (
          <DraftPreview result={result} onApply={onApply} onClose={onClose} />
        )}

        {phase === "error" && (
          <div className="space-y-4">
            <p className="text-sm text-destructive">{error}</p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Đóng
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setError(null);
                  setPhase("armed");
                }}
              >
                Thử lại
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DraftPreview({
  result,
  onApply,
  onClose,
}: {
  result: DictationResponse;
  onApply: (r: DictationResponse) => void;
  onClose: () => void;
}) {
  const { transcript, soap } = result;
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Provider:{" "}
        <strong className="font-semibold text-foreground">
          {transcript.provider}
        </strong>{" "}
        · Model:{" "}
        <strong className="font-semibold text-foreground">{soap.model}</strong>{" "}
        · Thời lượng: {transcript.durationSeconds.toFixed(1)}s
      </p>

      <Section title="S – Subjective" body={soap.subjective} />
      <Section title="O – Objective" body={soap.objective} />
      <Section title="A – Assessment" body={soap.assessment} />
      <Section title="P – Plan" body={soap.plan} />

      <details className="mt-3">
        <summary className="cursor-pointer text-xs text-muted-foreground">
          Xem bản phiên âm gốc
        </summary>
        <pre className="mt-2 whitespace-pre-wrap rounded-md bg-muted p-3 text-xs text-foreground">
          {transcript.text}
        </pre>
      </details>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Đóng (không áp dụng)
        </Button>
        <Button type="button" onClick={() => onApply(result)}>
          Áp dụng vào bệnh án
        </Button>
      </DialogFooter>
    </div>
  );
}

function Section({ title, body }: { title: string; body: string }) {
  return (
    <div className="space-y-1">
      <div className="text-sm font-semibold text-foreground">{title}</div>
      <div className="whitespace-pre-wrap rounded-md bg-muted p-2.5 text-sm text-foreground">
        {body || <em className="text-muted-foreground">(trống)</em>}
      </div>
    </div>
  );
}

function formatElapsed(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
